import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useI18n } from "@/lib/i18n";

// Imprint is translated (EN/DE/RO); the privacy policy is English only.
const COMPANY = {
  name: "Spotera Systems SRL",
  address: [
    "Strada Egretei nr. 31, Etaj 6, Ap. 53",
    "Municipiul Brașov, Județul Brașov",
  ],
  country: { en: "Romania", de: "Rumänien", ro: "România" },
  email: "contact@liberparking.eu",
};
const LAST_UPDATED = "29 September 2026";

const Section = ({ title, children }) => (
  <section className="mt-12">
    <h2 className="font-heading text-2xl font-semibold tracking-tight text-white mb-4">
      {title}
    </h2>
    <div className="space-y-4 text-[#A0A0A0] leading-relaxed">{children}</div>
  </section>
);

const Mail = () => (
  <a href={`mailto:${COMPANY.email}`} className="text-[#C2674A] hover:text-white transition-colors">
    {COMPANY.email}
  </a>
);

const Address = () => {
  const { lang } = useI18n();
  const lines = [...COMPANY.address, COMPANY.country[lang] || COMPANY.country.en];
  return (
    <p>
      {COMPANY.name}
      {lines.map((line) => (
        <React.Fragment key={line}>
          <br />
          {line}
        </React.Fragment>
      ))}
    </p>
  );
};

const LegalLayout = ({ overline, title, testId, englishOnly = false, children }) => {
  const { t, lang } = useI18n();
  return (
    <div className="min-h-screen bg-[#1A1C1F] text-white">
      <Header />
      <main className="pt-32 pb-24" data-testid={testId}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="font-mono text-xs tracking-[0.2em] uppercase text-[#C2674A] mb-5">
            / {overline}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter leading-none">
            {title}
          </h1>
          {englishOnly && lang !== "en" && (
            <p className="mt-6 text-sm text-[#A0A0A0] italic">{t.legal.englishOnly}</p>
          )}
          {children}
          <p className="mt-16 font-mono text-xs text-[#A0A0A0] tracking-wider">
            {t.legal.updated}: {LAST_UPDATED}
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 border border-[#3A3E43] text-white hover:border-white transition-colors duration-200 font-medium"
          >
            <ArrowLeft size={16} /> {t.legal.back}
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const IMPRINT = {
  en: {
    overline: "Legal notice",
    operator: "Website operator",
    brand: "LìBER is a brand of Spotera Systems SRL.",
    representative: "Legal representative",
    representativeText: "Andreea Silvana Speianu, Administrator",
    registration: "Company registration",
    registrationLines: [
      "Registered with the Trade Register Office attached to the Brașov Tribunal (Oficiul Registrului Comerțului de pe lângă Tribunalul Brașov)",
      "Trade Register number: J2026033678001",
      "European Unique Identifier (EUID): ROONRC.J2026033678001",
      "Unique Registration Code (CUI): 54743900",
      "EU VAT identification number: RO55579946",
      "Share capital: 5,000 RON",
    ],
    contact: "Contact",
    email: "Email",
    formBefore: "or use the ",
    formLink: "contact form",
    formAfter: " on our website.",
    liability: "Liability for content and links",
    liabilityText: [
      "We prepare the content of this website with care, but cannot guarantee that it is complete, accurate or up to date. Product information and prices shown are indicative and do not constitute a binding offer.",
      "Where this website links to external websites, their operators are solely responsible for their content. We had no indication of unlawful content when the links were set and will remove any such link as soon as we become aware of it.",
    ],
    copyright: "Copyright",
    copyrightText:
      "Texts, images, product renders and the design of this website are protected by copyright. Any use beyond what the law permits requires our prior written consent.",
  },
  de: {
    overline: "Rechtliche Hinweise",
    operator: "Anbieter der Website",
    brand: "LìBER ist eine Marke der Spotera Systems SRL.",
    representative: "Vertreten durch",
    representativeText: "Andreea Silvana Speianu, Geschäftsführung (Administrator)",
    registration: "Registereintrag",
    registrationLines: [
      "Eingetragen beim Handelsregisteramt am Landgericht Brașov (Oficiul Registrului Comerțului de pe lângă Tribunalul Brașov)",
      "Handelsregisternummer: J2026033678001",
      "Europäische einheitliche Kennung (EUID): ROONRC.J2026033678001",
      "Rumänische Registrierungsnummer (CUI): 54743900",
      "Umsatzsteuer-Identifikationsnummer: RO55579946",
      "Stammkapital: 5.000 RON",
    ],
    contact: "Kontakt",
    email: "E-Mail",
    formBefore: "oder über das ",
    formLink: "Kontaktformular",
    formAfter: " auf unserer Website.",
    liability: "Haftung für Inhalte und Links",
    liabilityText: [
      "Wir erstellen die Inhalte dieser Website mit Sorgfalt, können jedoch keine Gewähr für Vollständigkeit, Richtigkeit und Aktualität übernehmen. Produktangaben und Preise sind unverbindlich und stellen kein verbindliches Angebot dar.",
      "Für Inhalte verlinkter externer Websites sind ausschließlich deren Betreiber verantwortlich. Zum Zeitpunkt der Verlinkung waren keine rechtswidrigen Inhalte erkennbar. Sobald uns Rechtsverletzungen bekannt werden, entfernen wir den betreffenden Link umgehend.",
    ],
    copyright: "Urheberrecht",
    copyrightText:
      "Texte, Bilder, Produktvisualisierungen und das Design dieser Website sind urheberrechtlich geschützt. Jede Nutzung über die gesetzlichen Grenzen hinaus bedarf unserer vorherigen schriftlichen Zustimmung.",
  },
  ro: {
    overline: "Mențiuni legale",
    operator: "Operatorul site-ului",
    brand: "LìBER este o marcă a Spotera Systems SRL.",
    representative: "Reprezentant legal",
    representativeText: "Speianu Andreea Silvana, administrator",
    registration: "Date de înregistrare",
    registrationLines: [
      "Înregistrată la Oficiul Registrului Comerțului de pe lângă Tribunalul Brașov",
      "Nr. de ordine în registrul comerțului: J2026033678001",
      "Identificator unic la nivel european (EUID): ROONRC.J2026033678001",
      "Cod Unic de Înregistrare (CUI): 54743900",
      "Cod de înregistrare în scopuri de TVA: RO55579946",
      "Capital social: 5.000 lei",
    ],
    contact: "Contact",
    email: "E-mail",
    formBefore: "sau prin ",
    formLink: "formularul de contact",
    formAfter: " de pe site.",
    liability: "Răspunderea pentru conținut și linkuri",
    liabilityText: [
      "Pregătim cu grijă conținutul acestui site, dar nu putem garanta că este complet, corect sau actualizat. Informațiile despre produse și prețurile afișate sunt orientative și nu constituie o ofertă fermă.",
      "Pentru conținutul site-urilor externe către care trimitem prin linkuri răspund exclusiv operatorii acestora. La momentul plasării linkurilor nu am avut indicii privind conținut ilegal și vom elimina orice astfel de link imediat ce aflăm despre el.",
    ],
    copyright: "Drepturi de autor",
    copyrightText:
      "Textele, imaginile, randările de produs și designul acestui site sunt protejate prin drepturi de autor. Orice utilizare dincolo de limitele permise de lege necesită acordul nostru prealabil, în scris.",
  },
};

export const Imprint = () => {
  const { t, lang } = useI18n();
  const c = IMPRINT[lang] || IMPRINT.en;
  return (
    <LegalLayout overline={c.overline} title={t.footer.imprint} testId="imprint-page">
      <Section title={c.operator}>
        <Address />
        <p>{c.brand}</p>
      </Section>

      <Section title={c.representative}>
        <p>{c.representativeText}</p>
      </Section>

      <Section title={c.registration}>
        <p>
          {c.registrationLines.map((line, i) => (
            <React.Fragment key={line}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </p>
      </Section>

      <Section title={c.contact}>
        <p>
          {c.email}: <Mail />
          <br />
          {c.formBefore}
          <Link to="/#contact" className="text-[#C2674A] hover:text-white transition-colors">
            {c.formLink}
          </Link>
          {c.formAfter}
        </p>
      </Section>

      <Section title={c.liability}>
        {c.liabilityText.map((para) => (
          <p key={para}>{para}</p>
        ))}
      </Section>

      <Section title={c.copyright}>
        <p>{c.copyrightText}</p>
      </Section>
    </LegalLayout>
  );
};

export const Privacy = () => {
  const { t } = useI18n();
  return (
    <LegalLayout overline="Data protection" title={t.footer.privacy} testId="privacy-page" englishOnly>
      <Section title="1. Controller">
        <p>
          The controller responsible for processing personal data on this website within the
          meaning of the General Data Protection Regulation (GDPR) is:
        </p>
        <Address />
        <p>
          Email: <Mail />
        </p>
      </Section>

      <Section title="2. Summary">
        <p>
          The protection of your personal data is important to us. We treat personal data
          confidentially and in accordance with the statutory data protection regulations and
          this privacy policy. Personal data is any data that can be used to identify you
          personally.
        </p>
        <p>
          This website does not use cookies, analytics, tracking pixels or advertising tools.
          Fonts are served from our own server; no content is loaded from third-party
          services. We only process personal data when you visit the site (technical server
          logs) and when you contact us through the contact form.
        </p>
      </Section>

      <Section title="3. Hosting and server logs">
        <p>
          The website is hosted by an external hosting provider in the European Union. When
          you visit the site, the web server
          automatically records technical information that your browser sends: IP address,
          date and time of the request, the page requested, HTTP status code, amount of data
          transferred, referrer URL and browser/operating system (user agent).
        </p>
        <p>
          We use this data only to deliver the website, keep it secure (for example to detect
          and limit abuse of the contact form) and fix errors. Legal basis: Art. 6(1)(f) GDPR —
          our legitimate interest in a secure and working website. Log files are deleted
          automatically after 14 days at the latest, unless a specific security incident
          requires us to keep them longer.
        </p>
      </Section>

      <Section title="4. Contact form">
        <p>
          If you send us a message through the contact form, we process the data you enter:
          name, email address, your role, company (optional) and your message, plus the time
          of the request. The form data is sent to us as an email to <Mail /> via our email
          provider. It is not stored in a database on the website server.
        </p>
        <p>
          We use this data only to answer your enquiry and, if applicable, to prepare a
          business relationship. Legal basis: Art. 6(1)(b) GDPR (steps prior to entering into
          a contract) and Art. 6(1)(f) GDPR (our legitimate interest in answering enquiries).
          We delete your enquiry once it has been dealt with, unless we are legally required
          to keep it longer (for example because it led to a contract).
        </p>
        <p>
          Providing this data is voluntary, but without an email address we cannot reply.
        </p>
      </Section>

      <Section title="5. Language setting in your browser">
        <p>
          When you choose a language, the website stores your choice under the key
          “liber_lang” in your browser's local storage so the site appears in the same
          language on your next visit. This information stays on your device and is not
          transmitted to us. You can delete it at any time through your browser settings.
        </p>
      </Section>

      <Section title="6. Recipients and transfers">
        <p>
          We do not sell your data or share it with third parties for marketing. Data is only
          processed by the service providers that host this website and deliver our email. We
          do not intend to transfer personal data to countries outside the European Economic
          Area.
        </p>
      </Section>

      <Section title="7. Your rights">
        <p>
          Under the GDPR you have the right to access your personal data (Art. 15), to have
          it corrected (Art. 16) or deleted (Art. 17), to restrict its processing (Art. 18),
          to data portability (Art. 20) and to object to processing based on our legitimate
          interests (Art. 21). To exercise these rights, write to <Mail />.
        </p>
        <p>
          You also have the right to lodge a complaint with a supervisory authority, in
          particular in the EU member state of your residence or place of work. Our competent
          authority is the Romanian National Supervisory Authority for Personal Data
          Processing (ANSPDCP), B-dul G-ral. Gheorghe Magheru nr. 28-30, Sector 1, Bucharest,
          Romania — www.dataprotection.ro.
        </p>
      </Section>

      <Section title="8. Automated decisions and changes">
        <p>
          We do not use automated decision-making or profiling. We will update this policy
          when the website or the legal requirements change; the current version is always
          available on this page.
        </p>
      </Section>
    </LegalLayout>
  );
};
